import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, Users, ChevronRight } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CoursesPage() {
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    student_name: '',
    student_age: '',
    parent_name: '',
    email: '',
    phone: '',
    notes: ''
  });

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/courses`);
        setCourses(res.data);
      } catch (e) {
        console.error('Failed to load courses', e);
      }
    };
    load();
  }, []);

  const openBooking = (course) => {
    setSelectedCourse(course);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCourse) return;
    setLoading(true);

    try {
      const data = {
        course_id: selectedCourse.id,
        student_name: form.student_name,
        student_age: form.student_age ? parseInt(form.student_age) : null,
        parent_name: form.parent_name || null,
        email: form.email,
        phone: form.phone,
        notes: form.notes || null
      };

      await axios.post(`${API}/bookings`, data);
      toast.success('Richiesta inviata! La nostra segretaria ti contatterà per comunicarti i giorni disponibili.');
      setDialogOpen(false);
      setForm({
        student_name: '', student_age: '', parent_name: '',
        email: '', phone: '', notes: ''
      });
    } catch (err) {
      toast.error('Errore nella richiesta. Riprova.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <div className="relative bg-[#0288D1] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-40 h-40 rounded-full bg-white" />
          <div className="absolute bottom-10 right-20 w-60 h-60 rounded-full bg-[#FFCA28]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-20">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#FFCA28] mb-2">Le Nostre Discipline</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-white">
            I Nostri Corsi
          </h1>
          <p className="text-base md:text-lg text-blue-100 mt-3 max-w-xl">
            Tante discipline per tutte le età. La prima lezione è sempre gratuita! Lascia i tuoi dati e ti ricontatteremo.
          </p>
        </div>
      </div>

      {/* Courses Grid */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {courses.map(course => (
            <div
              key={course.id}
              data-testid={`course-item-${course.id}`}
              className="card-hover bg-white rounded-3xl overflow-hidden border border-slate-100 shadow-xl shadow-slate-100/50"
            >
              <div className="product-image-container aspect-[16/10]">
                <img src={course.image_url} alt={course.name} className="w-full h-full object-cover" />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 flex-wrap mb-3">
                  {course.age_group && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-[#FFCA28]/20 text-[#FF7043] text-xs font-bold rounded-full">
                      <Users size={12} /> {course.age_group}
                    </span>
                  )}
                </div>
                <h3 className="font-heading font-bold text-xl text-slate-900 mb-2">{course.name}</h3>
                <p className="text-sm text-slate-500 leading-relaxed mb-4">{course.description}</p>
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-1 text-sm font-bold text-green-600">
                    <Calendar size={14} /> Prova Gratuita
                  </span>
                  <button
                    data-testid={`book-course-${course.id}`}
                    onClick={() => openBooking(course)}
                    className="inline-flex items-center gap-1 px-5 py-2.5 bg-[#FF7043] text-white font-heading font-bold text-sm rounded-full btn-caribbean"
                  >
                    Prenota <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Booking Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="booking-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              Richiedi Lezione di Prova
            </DialogTitle>
            <DialogDescription>
              {selectedCourse && (
                <span className="text-[#FF7043] font-semibold">{selectedCourse.name}</span>
              )}
              {' — '} Lascia i tuoi dati e la nostra segretaria ti contatterà per comunicarti i giorni disponibili!
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="student_name">Nome Studente *</Label>
                <Input
                  id="student_name"
                  data-testid="booking-student-name"
                  required
                  value={form.student_name}
                  onChange={e => setForm(f => ({ ...f, student_name: e.target.value }))}
                  placeholder="Mario Rossi"
                />
              </div>
              <div>
                <Label htmlFor="student_age">Età</Label>
                <Input
                  id="student_age"
                  data-testid="booking-student-age"
                  type="number"
                  min="3"
                  max="99"
                  value={form.student_age}
                  onChange={e => setForm(f => ({ ...f, student_age: e.target.value }))}
                  placeholder="12"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="parent_name">Nome Genitore (se minorenne)</Label>
              <Input
                id="parent_name"
                data-testid="booking-parent-name"
                value={form.parent_name}
                onChange={e => setForm(f => ({ ...f, parent_name: e.target.value }))}
                placeholder="Nome del genitore"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  data-testid="booking-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="email@esempio.it"
                />
              </div>
              <div>
                <Label htmlFor="phone">Telefono *</Label>
                <Input
                  id="phone"
                  data-testid="booking-phone"
                  required
                  value={form.phone}
                  onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                  placeholder="380 123 4567"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="notes">Note aggiuntive</Label>
              <Textarea
                id="notes"
                data-testid="booking-notes"
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Esperienza di danza, esigenze particolari..."
                rows={3}
              />
            </div>

            <div className="flex items-start gap-2">
              <input type="checkbox" id="privacy-consent" required className="mt-1 accent-[#FF7043]" data-testid="booking-privacy-consent" />
              <label htmlFor="privacy-consent" className="text-xs text-slate-500">
                Accetto il trattamento dei miei dati personali in conformità alla <a href="/privacy" target="_blank" className="text-[#0288D1] underline">Privacy Policy</a> per la gestione della mia richiesta. *
              </label>
            </div>

            <button
              type="submit"
              data-testid="booking-submit-btn"
              disabled={loading}
              className="w-full py-3.5 bg-[#FF7043] text-white font-heading font-bold text-base rounded-full btn-caribbean disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Invio in corso...' : 'Invia Richiesta'}
            </button>

            <p className="text-xs text-slate-400 text-center">
              La nostra segretaria ti ricontatterà per comunicarti i giorni e gli orari disponibili.
            </p>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
